import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Video,
  Sparkles,
  Handshake,
  Shield,
  UserSearch,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDict } from "@/lib/i18n/context";
import { isReportsAdmin } from "@/lib/reports-admin";

export function SidebarNav({
  userEmail,
  collapsed = false,
  onNavigate,
}: {
  userEmail?: string | null;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const t = useDict();
  const { pathname } = useLocation();
  const links = [
    { href: "/dashboard", label: t.nav.dashboard, icon: LayoutDashboard },
    { href: "/jobs", label: t.nav.jobs, icon: Briefcase },
    { href: "/candidates", label: t.nav.candidates, icon: Users },
    { href: "/talent", label: t.nav.talent, icon: UserSearch },
    { href: "/interviews", label: t.nav.interviews, icon: Video },
    { href: "/partners", label: t.nav.partners, icon: Handshake },
    ...(isReportsAdmin(userEmail)
      ? [{ href: "/reports", label: t.nav.reports, icon: Shield }]
      : []),
  ];
  return (
    <nav className="flex flex-col gap-1">
      {links.map(({ href, label, icon: Icon }) => {
        const active =
          href === "/dashboard"
            ? pathname === href
            : pathname.startsWith(href);
        return (
          <Link
            key={href}
            to={href}
            title={label}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
              collapsed && "justify-center px-2",
              active
                ? "bg-brand-600 text-white"
                : "text-muted hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-950 dark:hover:text-brand-200",
            )}
          >
            <Icon className="h-5 w-5 shrink-0" />
            {!collapsed && <span className="truncate">{label}</span>}
          </Link>
        );
      })}
    </nav>
  );
}

export function Brand({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const t = useDict();
  return (
    <Link
      to="/dashboard"
      onClick={onNavigate}
      title={t.common.appName}
      className={cn(
        "flex min-w-0 items-center gap-2.5 px-1",
        collapsed && "justify-center px-0",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white">
        <Sparkles className="h-5 w-5" />
      </span>
      {!collapsed && (
        <span className="truncate text-lg font-bold tracking-wide">
          {t.common.appName}
        </span>
      )}
    </Link>
  );
}
